import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation, useAsync } from '@util/hooks';
import LoginTitle from './LoginTitle';
import { loginErrorMsg } from '@components/style';
import { TextInput, Button } from '@components';
import cx from 'classnames';
import { LoginDispatchContext } from '../index';
import { setNextStep, setStep, initialState } from '../reducer';
import { fetchCheckOtp, fetchSendOtp } from '@api/login';
import { secToHms } from '@util/common/util';
import { useDispatch } from 'react-redux';
import { setAdditionalInfo } from '@reducer/UserInfo';

const INPUT_TIME_LIMIT = 300;
const secToStr = sec => {
    const hms = secToHms(sec);
    return hms.m.toString().padStart(2, '0') + ':' + hms.s.toString().padStart(2, '0');
};

const FindForm = () => {
    const storeDispatch = useDispatch();
    const t = useTranslation('Login');
    const timeoutRef = useRef();
    const dispatch = useContext(LoginDispatchContext);
    const [formData, setFormData] = useState({ email: '', verificationCode: '' });
    const [checkVerificationCode, setCheckVerificationCode] = useState(false);
    const [showVerificationErrorMsg, setShowVerificationErrorMsg] = useState({
        err: false,
        msg: 'Please check the verification code',
    });
    const { promise: getOtpNumber, state: getOtpNumberState } = useAsync({
        promise: fetchSendOtp,
        resolve: res => {
            if (res.code === '0') {
                setRestTime(INPUT_TIME_LIMIT);
                startTimeout();
                setShowVerificationErrorMsg({ err: false, msg: '' });
            } else if (res.code === '1102' && res.errorResponse === 'userID') {
                setShowVerificationErrorMsg({ err: true, msg: 'This is not a registered E-Mail.' });
            } else {
                setShowVerificationErrorMsg({ err: true, msg: 'Issuing the verification code failed.' });
            }
        },
        reject: err => {
            if (err.data && err.data.code === '1102' && err.data.errorResponse === 'userID') {
                setShowVerificationErrorMsg({ err: true, msg: 'This is not a registered E-Mail.' });
            } else {
                setShowVerificationErrorMsg({ err: true, msg: 'Issuing the verification code failed.' });
            }
            console.log(err);
        },
    });
    const { promise: checkOtpNumber, state: checkOtpNumberState } = useAsync({
        promise: fetchCheckOtp,
        resolve: res => {
            if (res.authResult) {
                const {
                    userCompany: { uuid, secret },
                } = res;
                storeDispatch(setAdditionalInfo({ companyInfo: { uuid, secret } }));
                dispatch(setNextStep({ email: checkOtpNumberState.request.userID }));
            } else {
                setShowVerificationErrorMsg({ err: true, msg: 'Please check the verification code' });
            }
        },
        reject: err => {
            console.log(err);
        },
    });

    const [restTime, setRestTime] = useState(INPUT_TIME_LIMIT);
    const stopTimeout = () => {
        if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
            timeoutRef.current = null;
        }
    };
    const startTimeout = () => {
        stopTimeout();
        timeoutRef.current = setInterval(() => {
            let prevRestTime = INPUT_TIME_LIMIT;
            setRestTime(prev => {
                prevRestTime = prev;
                return prev ? prev - 1 : prev;
            });
            if (!prevRestTime) {
                stopTimeout();
            }
        }, 1000);
    };

    const handleFormChange = e => {
        const target = e.currentTarget;
        setFormData({ ...formData, [target.name]: target.value });
    };

    const handleRequestCode = () => {
        if (!formData.email) {
            setShowVerificationErrorMsg({ err: true, msg: 'Please enter your e-mail' });
            return false;
        }
        getOtpNumber({ userID: formData.email });
    };

    const handleCheckCode = () => {
        if (restTime <= 0) {
            setShowVerificationErrorMsg({ err: true, msg: 'Time limit exceeded' });
        } else {
            checkOtpNumber({
                userID: formData.email,
                otpNum: formData.verificationCode,
            });
        }
    };

    const handleClick = () => {
        dispatch(setStep(initialState));
    };

    useEffect(() => {
        if (formData.verificationCode.length === 6) {
            setCheckVerificationCode(true);
        } else {
            setCheckVerificationCode(false);
        }
    }, [formData.verificationCode]);

    useEffect(() => {
        return () => {
            stopTimeout();
        };
    }, []);

    return (
        <>
            <LoginTitle
                title={t('Forget Password')}
                subTitle={
                    <>
                        <div>{t('Please enter your email address when registering.')}</div>
                        <div>{t('We will send you a verification code to reset your password.')}</div>
                    </>
                }
            />
            <div className="cont-box">
                <div className="flx-col gap-3">
                    <div className={'cont--error'} style={!showVerificationErrorMsg.err ? loginErrorMsg : {}}>
                        <p>* {t(showVerificationErrorMsg.msg)}</p>
                    </div>

                    <div className="cont--input flx-col flx-full gap-3">
                        <div className={'flx-row gap-2'}>
                            <TextInput
                                inputGroupClassName={'w-100'}
                                type={'email'}
                                name={'email'}
                                placeholder={t('Please enter your e-mail')}
                                value={formData.email}
                                handleChange={handleFormChange}
                            />
                            <Button className="btn-secondary flx-shrink-0" onClick={handleRequestCode}>
                                {t('Send verification code')}
                            </Button>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <TextInput
                                inputGroupClassName={'w-100'}
                                type={'text'}
                                maxlength={6}
                                name={'verificationCode'}
                                placeholder={t('Please enter the verification code')}
                                value={formData.verificationCode}
                                handleChange={handleFormChange}
                            />
                            <div
                                className={cx({ 'd-none': !getOtpNumberState.response })}
                                // className={cx({ 'd-none': !getOtpNumberState.response || getOtpNumberState.isLoading })}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 8,
                                    height: '38px',
                                    color: '#000',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {secToStr(restTime)}
                            </div>
                        </div>
                    </div>
                    <div className={cx('cont--btn', !checkVerificationCode && 'disabled')}>
                        <Button
                            className={cx('btn-brand btn-large w-100', checkVerificationCode && 'active')}
                            disabled={!checkVerificationCode}
                            onClick={handleCheckCode}
                        >
                            {t('Check Verification Code')}
                        </Button>
                    </div>
                </div>
            </div>
            <div className="bottom-box">
                <div className="bottom-box__wrap">
                    <div className="to-login">
                        <div className="opacity-5">{t('Did you find a password?')}</div>
                        <Button onClick={handleClick}>{t('Login')}</Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FindForm;
